// src/components/lists/DoctorList.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Star, MapPin,Video, Calendar, 
  CheckCircle,  Award 
} from 'lucide-react';

const DoctorList = ({ doctors }) => {
  const navigate = useNavigate();

  if (!doctors || doctors.length === 0) {
    return null;
  }

  const handleBookConsultation = (doctorId) => {
    // Navigate to booking page (you'll need to implement this route)
    navigate(`/book-consultation/${doctorId}`);
  };

  return (
    <div className="space-y-4">
      {doctors.map((doctor) => (
        <DoctorCard 
          key={doctor._id} 
          doctor={doctor}
          onBook={handleBookConsultation}
        />
      ))}
    </div>
  );
};

const DoctorCard = ({ doctor, onBook }) => {
  const {
    _id,
    title = 'Dr.',
    firstName,
    lastName,
    specialization,
    experience,
    rating,
    consultationFee,
    hospital,
    isAvailableOnline,
    isAvailablePhysical,
    about,
    qualifications,
    languages,
    isVerified
  } = doctor;

  const fullName = `${title} ${firstName} ${lastName}`;
  const averageRating = rating?.average || 0;
  const totalReviews = rating?.count || 0;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
      <div className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Doctor Avatar/Image */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
              <span className="text-4xl font-bold text-primary-600">
                {firstName?.charAt(0)}{lastName?.charAt(0)}
              </span>
            </div>
          </div>

          {/* Doctor Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-gray-900">
                    {fullName}
                  </h3>
                  {isVerified && (
                    <CheckCircle className="w-5 h-5 text-blue-500" title="Verified Doctor" />
                  )}
                </div>
                <p className="text-primary-600 font-medium">{specialization}</p>
              </div>
              
              {/* Rating */}
              {averageRating > 0 && (
                <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1 rounded-full">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold text-gray-900">{averageRating.toFixed(1)}</span>
                  <span className="text-sm text-gray-600">({totalReviews})</span>
                </div>
              )}
            </div>

            {/* Experience & Hospital */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
              {experience > 0 && (
                <div className="flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  <span>{experience} years experience</span>
                </div>
              )}
              {hospital?.name && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{hospital.name}</span>
                </div>
              )}
            </div>

            {/* Qualifications */}
            {qualifications && qualifications.length > 0 && (
              <div className="mb-3">
                <div className="flex flex-wrap gap-2">
                  {qualifications.slice(0, 3).map((qual, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                    >
                      {typeof qual === 'string' ? qual : qual.degree}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* About */}
            {about && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {about}
              </p>
            )}

            {/* Languages */}
            {languages && languages.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <span className="font-medium">Languages:</span>
                <span>{languages.join(', ')}</span>
              </div>
            )}

            {/* Availability & Consultation Options */}
            <div className="flex flex-wrap gap-3 mb-4">
              {isAvailableOnline && (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                  <Video className="w-4 h-4" />
                  <span>Online Available</span>
                </div>
              )}
              {isAvailablePhysical && (
                <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>In-Person Available</span>
                </div>
              )}
            </div>

            {/* Consultation Fees & Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-4">
                {consultationFee?.online > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Online Consultation</p>
                    <p className="text-lg font-bold text-primary-600">
                      KES {consultationFee.online.toLocaleString()}
                    </p>
                  </div>
                )}
                {consultationFee?.physical > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Physical Consultation</p>
                    <p className="text-lg font-bold text-primary-600">
                      KES {consultationFee.physical.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onBook(_id)}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Book Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorList;